// FundingHubCard — "Send to Funding Hub" on the client Overview (Tanya 2026-10-26).
//
// Shows the client's middle bureau score against the company's Funding Ready
// minimum. When it clears the bar, staff can send the client to the Alief
// Funding Hub as a new lead (contact details + scores go with them). After a
// send, the card shows when it went and the lead's current funding status,
// fetched live from the Funding Hub when the card opens.
import { useEffect, useState } from "react";
import { Landmark } from "lucide-react";
import { fundingHub, type FundingHubState } from "../lib/api";

const STATUS_LABEL: Record<string, string> = {
  pending: "New lead", onboarding: "Onboarding", active: "Active", needs_review: "Needs review",
  paused: "Paused", completed: "Funded / completed", churned: "Closed", suspended: "Suspended",
};

export function FundingHubCard({ clientId, canSend }: { clientId: string; canSend: boolean }) {
  const [state, setState] = useState<FundingHubState | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState(null); setLoadErr(null); setMsg(null); setErr(null);
    fundingHub.get(clientId, true)
      .then((r) => { if (!cancelled) setState(r); })
      .catch((e) => { if (!cancelled) setLoadErr(e?.message || "Could not load Funding Hub status"); });
    return () => { cancelled = true; };
  }, [clientId]);

  async function send() {
    if (!state) return;
    const again = state.sent_at ? " again" : "";
    if (!confirm(`Send this client to the Alief Funding Hub${again} as a funding lead? Their name, contact details, address and bureau scores will be shared with the Funding Hub (the credit report itself stays here).`)) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await fundingHub.send(clientId);
      setMsg(r.handoff.created ? "Sent. A new lead was created in the Funding Hub." : "Sent. The Funding Hub already had this client, so their record was updated.");
      const fresh = await fundingHub.get(clientId, true).catch(() => null);
      if (fresh) setState(fresh);
    } catch (e: any) {
      setErr(e?.message || "Send failed");
    } finally {
      setBusy(false);
    }
  }

  if (loadErr) return <div className="panel"><p className="error-text small">Funding Hub: {loadErr}</p></div>;
  if (!state) return null;

  const { eligible, middle_score, threshold, scores, handoff, live, sent_at } = state;
  // Only Funding Ready clients (or ones already sent) get the card at all.
  if (!eligible && !sent_at) return null;
  const liveStatus = live?.status ? (STATUS_LABEL[live.status] ?? live.status) : null;

  return (
    <div className="panel" style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Landmark size={18} aria-hidden="true" /> Funding Hub
        </h3>
        {sent_at && (
          <span className="pill" title={new Date(sent_at).toLocaleString()}>
            Sent to Funding Hub · {new Date(sent_at).toLocaleDateString()}
          </span>
        )}
      </div>

      <p className="small" style={{ margin: 0 }}>
        Middle score: <strong>{middle_score ?? "—"}</strong>
        {" · "}Funding Ready minimum: <strong>{threshold}</strong>
        {scores && (
          <span className="muted"> (EQ {scores.equifax ?? "—"} · EX {scores.experian ?? "—"} · TU {scores.transunion ?? "—"})</span>
        )}
      </p>

      {sent_at && (
        <p className="small" style={{ margin: 0 }}>
          Funding status:{" "}
          {live?.error
            ? <span className="muted">{live.error === "not_configured" ? "Funding Hub connection not set up" : live.error}</span>
            : live && live.found === false
              ? <span className="muted">not found in the Funding Hub (it may have been removed there)</span>
              : <strong>{liveStatus ?? STATUS_LABEL[handoff?.status ?? ""] ?? handoff?.status ?? "—"}</strong>}
          {live?.found && live.report_analyzed && <span className="muted"> · report analyzed</span>}
        </p>
      )}
      {handoff && !handoff.ghl_tagged && handoff.ghl_error && (
        <p className="small muted" style={{ margin: 0 }}>GoHighLevel tag not added: {handoff.ghl_error}</p>
      )}

      {!eligible && (
        <p className="muted small" style={{ margin: 0 }}>
          {middle_score == null
            ? "No analyzed credit report on file right now."
            : `The middle score is now below the Funding Ready minimum of ${threshold}.`}
        </p>
      )}

      {eligible && canSend && (
        <div>
          <button type="button" className="primary-button" onClick={send} disabled={busy || !state.configured}
            title={state.configured ? undefined : "The Funding Hub connection is not set up on this install yet."}>
            <Landmark size={16} aria-hidden="true" /> {busy ? "Sending…" : sent_at ? "Send to Funding Hub again" : "Send to Funding Hub"}
          </button>
        </div>
      )}
      {msg && <p className="small" role="status" style={{ margin: 0, color: "var(--ne-success)" }}>{msg}</p>}
      {err && <p className="error-text small" role="alert" style={{ margin: 0 }}>{err}</p>}
    </div>
  );
}
